import {GetWalletResponse_Tab_Coins_CoinsHeader} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wallet/v1/page/service.types';
import {
  COLORS,
  Icon,
  ProgressBar,
  PushNotificationIconName,
  Text,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useMemo} from 'react';
import {useShouldAnimateProgressBars} from './utils/useShouldAnimateProgressBars';

const PROGRESS_BAR_SMALL_WIDTH = 24;

export const CoinsWalletHeaderProgressBars: React.FC<
  Pick<
    GetWalletResponse_Tab_Coins_CoinsHeader,
    | 'deprecatedProgressBars'
    | 'deprecatedDescription'
    | 'deprecatedIsBonusClaimable'
  >
> = ({
  deprecatedProgressBars,
  deprecatedDescription,
  deprecatedIsBonusClaimable,
}) => {
  const shouldTriggerAnimation = useShouldAnimateProgressBars(
    deprecatedProgressBars,
  );

  const currentProgressBarIndex = useMemo(
    () =>
      deprecatedProgressBars.findIndex(progressBar => progressBar.isCurrent),
    [deprecatedProgressBars],
  );

  return (
    <Wrapper data-testid="coinsProgressBars">
      <GiftIconWrapper data-is-bonus-claimable={deprecatedIsBonusClaimable}>
        <Icon
          asset={PushNotificationIconName.Gift}
          size="XS"
          fill={COLORS.White}
        />
      </GiftIconWrapper>
      <ProgressBarWrapper
        data-animate={shouldTriggerAnimation}
        $activeIndex={currentProgressBarIndex}
      >
        {deprecatedProgressBars.map((progressBar, index) => (
          <StyledProgressBar
            key={index}
            color={COLORS.White}
            backgroundColor={COLORS['Fill.Grey.Secondary']}
            count={progressBar.availablePercentage}
            pendingCount={progressBar.pendingPercentage}
            height={12}
            total={100}
          />
        ))}
      </ProgressBarWrapper>
      {deprecatedDescription && (
        <Description
          size="px10"
          textAlignment="center"
          color="White"
          dangerouslySetInnerHTML={{__html: deprecatedDescription.text}}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 24px;
  overflow: hidden;
`;

const GiftIconWrapper = styled.div`
  position: relative;
  align-self: flex-end;
  margin-right: ${PROGRESS_BAR_SMALL_WIDTH}px;

  &[data-is-bonus-claimable='true'] {
    > svg {
      animation: bounce 2s infinite;

      @keyframes bounce {
        0%,
        70%,
        100% {
          transform: translateY(0);
        }
        10%,
        50% {
          transform: translateY(-2px);
        }
        55% {
          transform: translateY(0);
        }
        60% {
          transform: translateY(-2px);
        }
      }
    }

    &:after {
      position: absolute;
      top: 0;
      left: 10px;
      content: '';
      width: 6px;
      height: 6px;
      background-color: ${COLORS['Alerts.Success.Secondary']};
      border-radius: 50%;
    }
  }
`;

const ProgressBarWrapper = styled.div<{
  $activeIndex: number;
}>`
  display: flex;
  align-items: center;
  width: 100%;

  --progress-bar-width: calc(100% - ${PROGRESS_BAR_SMALL_WIDTH * 2}px);
  --final-x-position: ${props =>
    `calc(${props.$activeIndex} * var(--progress-bar-width) * -1 + ${PROGRESS_BAR_SMALL_WIDTH}px)`};

  &[data-animate='true'] {
    animation: activate 1s forwards;

    @keyframes activate {
      0% {
        transform: translateX(0);
      }
      70% {
        transform: translateX(
          calc(var(--final-x-position) - ${PROGRESS_BAR_SMALL_WIDTH}px)
        );
      }
      90% {
        transform: translateX(
          calc(var(--final-x-position) + ${PROGRESS_BAR_SMALL_WIDTH / 3}px)
        );
      }
      100% {
        transform: translateX(var(--final-x-position));
      }
    }
  }

  &[data-animate='false'] {
    transform: translateX(var(--final-x-position));
  }
`;

const StyledProgressBar = styled(ProgressBar)`
  flex: 0 0 var(--progress-bar-width);
`;

const Description = styled(Text)`
  margin-inline: 24px;
`;
